import { useEffect, useState } from 'react';

import { appelApi } from '../api/client';
import { IDENTITES_DEV, useSession } from '../auth/session';
import { CELLULE_NON_REPONDUE, COULEURS_NIVEAUX, LEGENDE, type CelluleNiveau } from '../composants/niveaux';
import { Message, dateLongue } from '../composants/elements';
import { NOM_PRODUIT } from '../produit';

/**
 * Page de connexion — implémentation de « Skills Matrix Home.dc.html ».
 *
 * Deux écarts assumés par rapport à la maquette :
 *
 * 1. Les six niveaux de l'échelle validée en séance (§4.1), et non quatre. La
 *    maquette proposait Aware / Practitioner / Expert / Mentor ; l'échelle du
 *    projet va de 0 à 5 avec preuve obligatoire à partir de 3, et c'est elle
 *    que l'API applique.
 * 2. Les chiffres du bandeau viennent d'une route publique volontairement
 *    limitée à des agrégats non nominatifs. Pas d'effectif affiché avant
 *    connexion : un outil de données nominatives ne se vante pas de son nombre
 *    de fiches sur sa page publique.
 */

type Accueil = {
  technologies: number;
  radars: string[];
  trimestre: string | null;
  editionRadar: string | null;
  echeance: string | null;
  domaineAutorise: string;
  authMode: 'entra' | 'dev';
};

const LIBELLES_RADAR: Record<string, string> = { tech: 'Tech', ai: 'AI' };

function LogoMicrosoft(): JSX.Element {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8" height="8" fill="#F25022" />
      <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
      <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
      <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}

function MarqueDecorative(): JSX.Element {
  return (
    <svg viewBox="0 0 400 560" aria-hidden="true" className="accueil__marque">
      <circle cx="200" cy="195" r="170" fill="#05BAEE" />
      <circle cx="200" cy="365" r="170" fill="#D6007F" />
      <path d="M52.8 280 A170 170 0 0 1 347.2 280 A170 170 0 0 1 52.8 280 Z" fill="#004F99" />
    </svg>
  );
}

const USAGES = [
  {
    teinte: '#05BAEE',
    titre: 'Staff an engagement',
    texte:
      'Know who can actually do the work — technology, level, evidence — instead of asking around.',
  },
  {
    teinte: '#004F99',
    titre: 'See the gaps',
    texte:
      'A technology nobody masters, or one that rests on a single person, while we keep selling it.',
  },
  {
    teinte: '#D6007F',
    titre: 'Plan training',
    texte:
      'Turn the gaps into a plan with named owners, and check next quarter whether it moved.',
  },
  {
    teinte: 'var(--border-strong)',
    titre: 'Read the radar differently',
    texte:
      'A technology in adopt that nobody masters is a delivery risk the radar alone does not show.',
  },
];

/** Niveaux affichés avant connexion : la même échelle que l'API sert ensuite. */
const NIVEAUX = [
  { niveau: 0, titre: 'Never used', texte: 'I have heard of it, I have never used it.' },
  { niveau: 1, titre: 'Basics', texte: 'Training, tutorial, personal proof of concept.' },
  { niveau: 2, titre: 'Supervised', texte: 'Used on a project, with someone overseeing me.' },
  {
    niveau: 3,
    titre: 'Autonomous',
    texte: 'Used in production without supervision. Written evidence required.',
    preuve: true,
  },
  {
    niveau: 4,
    titre: 'Advanced',
    texte: 'Several projects, I review other people’s work. Written evidence required.',
    preuve: true,
  },
  {
    niveau: 5,
    titre: 'Reference',
    texte: 'I train others, I arbitrate the choices, people come to me. Written evidence required.',
    preuve: true,
  },
];

/**
 * Aperçu de la grille. Contenu d'illustration, explicitement signalé comme tel :
 * aucune donnée réelle ne sort avant authentification.
 */
const APERCU_TECHNOS = ['Python', 'Spark', 'Airflow', 'PyTorch', 'MLOps', 'Kubernetes', 'React', 'NLP'];

const APERCU_LIGNES: { initiales: string; poste: string; niveaux: (number | null)[] }[] = [
  { initiales: 'A', poste: 'Lead data engineer', niveaux: [5, 4, 4, 1, 2, 3, null, 2] },
  { initiales: 'B', poste: 'ML engineer', niveaux: [4, 2, 1, 5, 3, 2, 1, 4] },
  { initiales: 'C', poste: 'NLP engineer', niveaux: [4, 1, 0, 3, 2, 1, null, 5] },
  { initiales: 'D', poste: 'Platform engineer', niveaux: [3, 2, 3, null, 4, 5, 2, null] },
  { initiales: 'E', poste: 'Analytics engineer', niveaux: [4, 3, 3, 1, 1, 2, 1, 1] },
  { initiales: 'F', poste: 'SRE', niveaux: [2, 1, 1, null, 3, 5, 0, null] },
];

function cellule(niveau: number | null): CelluleNiveau {
  return niveau === null ? CELLULE_NON_REPONDUE : (COULEURS_NIVEAUX[niveau] ?? CELLULE_NON_REPONDUE);
}

export function Connexion(): JSX.Element {
  const session = useSession();
  const [enAttente, setEnAttente] = useState(false);
  const [email, setEmail] = useState(IDENTITES_DEV[0]!.email);
  const [accueil, setAccueil] = useState<Accueil | undefined>(undefined);

  useEffect(() => {
    appelApi<Accueil>('/api/accueil')
      .then(setAccueil)
      .catch(() => setAccueil(undefined));
  }, []);

  async function connecte(identite?: string): Promise<void> {
    setEnAttente(true);
    try {
      await session.connecte(identite);
    } finally {
      setEnAttente(false);
    }
  }

  const domaine = `Elyadata accounts only (@${accueil?.domaineAutorise ?? 'elyadata.com'}).`;

  const boutonEntra = (grand: boolean): JSX.Element => (
    <button
      type="button"
      className={`bouton-entra${grand ? ' bouton-entra--grand' : ''}`}
      onClick={() => void connecte()}
      disabled={enAttente}
    >
      {enAttente ? <span className="rotor" aria-hidden="true" /> : <LogoMicrosoft />}
      <span>{enAttente ? 'Redirecting to Microsoft…' : 'Sign in with Microsoft'}</span>
    </button>
  );

  return (
    <div className="page">
      {/* --- En-tête ----------------------------------------------------- */}
      <header className="entete">
        <div className="contenu entete__barre">
          <a href="#haut" className="entete__marque">
            <img src="/assets/elyadata-logo.svg" alt="Elyadata" className="entete__logo" />
            <span className="entete__filet" aria-hidden="true" />
            <span className="entete__nom">{NOM_PRODUIT}</span>
          </a>
          <nav className="entete__nav" aria-label="Navigation">
            <a href="#usages" className="entete__lien">
              What it does
            </a>
            <a href="#apercu" className="entete__lien">
              Inside
            </a>
            <a href="#niveaux" className="entete__lien">
              Levels
            </a>
            {session.mode === 'entra' ? (
              <button
                type="button"
                className="bouton bouton--primaire bouton--petit"
                onClick={() => void connecte()}
                disabled={enAttente}
              >
                Sign in
              </button>
            ) : (
              <a href="#haut" className="entete__lien">
                Dev mode
              </a>
            )}
          </nav>
        </div>
      </header>

      <main className="page__corps">
        {/* --- Hero ------------------------------------------------------ */}
        <section id="haut" className="accueil__hero">
          <MarqueDecorative />
          <div className="contenu accueil__hero-interieur">
            <div className="apparait" style={{ maxWidth: 760 }}>
              <p className="eyebrow" style={{ marginBottom: 20 }}>
                Elyadata Engineering · internal tool
              </p>
              <h1 style={{ fontSize: 'clamp(36px, 6vw, 68px)', lineHeight: 1.05 }}>
                Who knows what,
                <br />
                written down.
              </h1>
              <p className="plomb" style={{ maxWidth: 620, marginTop: 24, fontSize: 'clamp(17px, 1.6vw, 20px)' }}>
                Every quarter, everyone declares their level on the technologies of the
                {accueil?.radars.length
                  ? ` ${accueil.radars.map((r) => LIBELLES_RADAR[r] ?? r).join(' and ')}`
                  : ''}{' '}
                radar{(accueil?.radars.length ?? 0) > 1 ? 's' : ''}. We use it to staff engagements,
                build the training plan, and read the radar with something better than memory.
              </p>

              <div className="accueil__connexion">
                {session.mode === 'entra' ? (
                  <>
                    {boutonEntra(true)}
                    <div className="pile" style={{ gap: 4 }}>
                      <span style={{ fontSize: 14, color: 'var(--fg-2)' }}>{domaine}</span>
                      <span className="discret">
                        Single sign-on via Microsoft Entra ID. No separate password.
                      </span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="pile" style={{ gap: 10, minWidth: 280 }}>
                      <label className="champ">
                        <span className="champ__libelle">Sign in as</span>
                        <select
                          className="selection"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        >
                          {IDENTITES_DEV.map((i) => (
                            <option key={i.email} value={i.email}>
                              {i.email} — {i.role}
                            </option>
                          ))}
                        </select>
                      </label>
                      <button
                        type="button"
                        className="bouton-entra"
                        onClick={() => void connecte(email)}
                        disabled={enAttente}
                      >
                        {enAttente ? <span className="rotor" aria-hidden="true" /> : <LogoMicrosoft />}
                        <span>{enAttente ? 'Signing in…' : 'Enter'}</span>
                      </button>
                    </div>
                    <div style={{ maxWidth: 340 }}>
                      <Message variante="attention">
                        Development mode: the identity comes from a header, with no verification.
                        The API refuses this mode in production.
                      </Message>
                    </div>
                  </>
                )}
              </div>

              {session.erreur ? (
                <div style={{ marginTop: 20, maxWidth: 560 }}>
                  <Message variante="erreur">{session.erreur}</Message>
                </div>
              ) : null}

              <div className="chiffres accueil__chiffres">
                <div className="chiffre">
                  <span className="chiffre__valeur">{accueil?.technologies ?? '—'}</span>
                  <span className="chiffre__libelle">Technologies tracked</span>
                </div>
                <div className="chiffre">
                  <span className="chiffre__valeur">{accueil?.radars.length ?? '—'}</span>
                  <span className="chiffre__libelle">Radars</span>
                </div>
                <div className="chiffre">
                  <span className="chiffre__valeur">{accueil?.trimestre ?? '—'}</span>
                  <span className="chiffre__libelle">Current campaign</span>
                </div>
                <div className="chiffre">
                  <span className="chiffre__valeur">6</span>
                  <span className="chiffre__libelle">Levels, evidence from 3</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* --- À quoi ça sert -------------------------------------------- */}
        <section id="usages" className="contenu" style={{ paddingBlock: 'clamp(48px, 8vw, 88px) clamp(16px, 3vw, 28px)' }}>
          <h2 style={{ maxWidth: 560, marginBottom: 'clamp(24px, 4vw, 44px)' }}>
            Four things it answers.
          </h2>
          <div className="usages">
            {USAGES.map((u, index) => (
              <div key={u.titre} className="usage" style={{ borderTopColor: u.teinte }}>
                <span className="mono" style={{ fontSize: 12, color: 'var(--fg-3)' }}>
                  {String(index + 1).padStart(2, '0')}
                </span>
                <h3 style={{ fontSize: 20, margin: '10px 0' }}>{u.titre}</h3>
                <p className="discret" style={{ fontSize: 15, lineHeight: 1.6 }}>
                  {u.texte}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* --- Aperçu de la grille --------------------------------------- */}
        <section id="apercu" className="section section--grise">
          <div className="contenu">
            <div className="entete-page" style={{ paddingTop: 0, borderBottom: 'none' }}>
              <div>
                <p className="eyebrow" style={{ marginBottom: 16 }}>
                  Inside the tool
                </p>
                <h2>One grid, per team.</h2>
              </div>
              <p className="plomb" style={{ maxWidth: 380, fontSize: 15 }}>
                Levels are declared by the person, and every level from 3 up comes with written
                evidence: a project, a release, a date.
              </p>
            </div>

            <div className="grille-apercu">
              <div className="grille-apercu__entete">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontFamily: 'var(--font-title)', fontSize: 15, fontWeight: 600 }}>
                    Data &amp; AI — preview
                  </span>
                  <span className="jeton">illustration, not real data</span>
                </div>
                <span className="mono discret" style={{ fontSize: 12 }}>
                  {accueil?.trimestre ? `Quarter ${accueil.trimestre}` : 'no campaign'}
                </span>
              </div>

              <div className="grille-apercu__defilement">
                <div className="grille-apercu__corps">
                  <div className="grille-apercu__ligne grille-apercu__ligne--titres">
                    <span className="grille-apercu__libelle">Collaborator</span>
                    {APERCU_TECHNOS.map((t) => (
                      <span key={t} className="grille-apercu__techno">
                        {t}
                      </span>
                    ))}
                  </div>

                  {APERCU_LIGNES.map((ligne) => (
                    <div key={ligne.initiales} className="grille-apercu__ligne">
                      <div className="grille-apercu__personne">
                        <span className="grille-apercu__pastille">{ligne.initiales}</span>
                        <span style={{ minWidth: 0 }}>
                          <span className="grille-apercu__nom">Collaborator {ligne.initiales}</span>
                          <span className="grille-apercu__poste">{ligne.poste}</span>
                        </span>
                      </div>
                      {ligne.niveaux.map((niveau, index) => {
                        const c = cellule(niveau);
                        return (
                          <span
                            key={`${ligne.initiales}-${index}`}
                            title={`${APERCU_TECHNOS[index]} — ${c.libelle}`}
                            className="grille-apercu__cellule"
                            style={{ background: c.fond, borderColor: c.bord }}
                          />
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>

              <div className="grille-apercu__legende">
                <span className="champ__libelle">Legend</span>
                {LEGENDE.map((l) => (
                  <span key={l.libelle} className="legende__entree">
                    <span
                      className="legende__pastille"
                      style={{ background: l.fond, borderColor: l.bord }}
                      aria-hidden="true"
                    />
                    {l.libelle}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* --- Les niveaux ----------------------------------------------- */}
        <section id="niveaux" className="contenu section">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'clamp(28px, 5vw, 72px)',
              alignItems: 'start',
            }}
          >
            <div>
              <p className="eyebrow" style={{ marginBottom: 18 }}>
                Proficiency
              </p>
              <h2>Six levels, defined by what you can be trusted with.</h2>
              <p className="plomb" style={{ marginTop: 22, maxWidth: 440, fontSize: 16 }}>
                A level is a statement about delivery, not about time spent. From 3 up, if you
                cannot point to the work that shows it, it is not that level yet — the tool refuses
                the line.
              </p>
              <p className="discret" style={{ marginTop: 18, maxWidth: 440 }}>
                And “not applicable” is not a 0: a 0 answers about a technology in your scope,
                “not applicable” says it is not part of your job.
              </p>
            </div>

            <div className="pile">
              {NIVEAUX.map((n) => {
                const c = COULEURS_NIVEAUX[n.niveau]!;
                return (
                  <div key={n.niveau} className="niveau">
                    <span
                      className="niveau__pastille"
                      style={{ background: c.fond, borderColor: c.bord }}
                      aria-hidden="true"
                    />
                    <div>
                      <h3 style={{ fontSize: 18, marginBottom: 6 }}>
                        {n.niveau} · {n.titre}
                      </h3>
                      <p className="discret" style={{ fontSize: 15, lineHeight: 1.6 }}>
                        {n.texte}
                      </p>
                    </div>
                    {n.preuve ? <span className="badge badge--nouveau">Evidence</span> : <span />}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="encart-cta">
            <div>
              <h3 style={{ fontSize: 'clamp(20px, 2.2vw, 26px)', marginBottom: 8 }}>
                Updating your profile takes twenty minutes, once a quarter.
              </h3>
              <p className="discret" style={{ fontSize: 15 }}>
                {domaine} Your levels are visible to management and to staffing, never to other
                collaborators.
                {accueil?.echeance ? ` Campaign deadline: ${dateLongue(accueil.echeance)}.` : ''}
              </p>
            </div>
            {session.mode === 'entra' ? (
              boutonEntra(true)
            ) : (
              <button
                type="button"
                className="bouton-entra bouton-entra--grand"
                onClick={() => void connecte(email)}
                disabled={enAttente}
              >
                <LogoMicrosoft />
                <span>Enter in dev mode</span>
              </button>
            )}
          </div>
        </section>
      </main>

      {/* --- Pied de page ------------------------------------------------ */}
      <footer className="pied">
        <div className="contenu pied__grille">
          <div style={{ maxWidth: 300 }}>
            <img src="/assets/elyadata-logo-white.svg" alt="Elyadata" className="pied__logo" />
            <p className="pied__texte">
              We unite scientific expertise and business acumen to crack the code of data
              transformation. La Marsa, Tunis.
            </p>
          </div>

          <div className="pied__colonne">
            <span className="pied__titre">{NOM_PRODUIT}</span>
            <a href="#usages">What it does</a>
            <a href="#apercu">Inside the tool</a>
            <a href="#niveaux">Proficiency levels</a>
            <a href="#haut">Sign in</a>
          </div>

          <div className="pied__colonne">
            <span className="pied__titre">Elyadata</span>
            <a href="https://radar.elyadata.com/">Technology Radar</a>
            <a href="https://www.elyadata.com/">What we do</a>
            <a href="https://www.linkedin.com/company/elyadata">LinkedIn</a>
            <a href="https://github.com/elyadata">Open source</a>
          </div>

          <div className="pied__colonne">
            <span className="pied__titre">Access</span>
            <p className="pied__texte">
              Sign-in is restricted to Elyadata Microsoft accounts and managed by IT.
            </p>
            <a href="mailto:it@elyadata.com" style={{ color: '#FF39A6' }}>
              Contact IT →
            </a>
          </div>
        </div>

        <div className="contenu">
          <div className="pied__bas">
            <span>
              © {new Date().getFullYear()} Elyadata. Internal tool — data stays inside the company.
            </span>
            <span className="mono">
              {accueil?.authMode === 'entra' ? 'Microsoft Entra ID SSO' : 'development mode'}
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
